import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Grid } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SplashPage,
  SubscriptionContext,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AddOnItemName,
  StripeSubscription,
  SubscriptionType,
} from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";

import ConfirmPaymentForm from "./components/ConfirmPaymentForm";
import InvoicePreview from "./components/InvoicePreview";
import PaymentForm from "./components/PaymentForm";
import PlanDetails from "./components/PlanDetails";
import SkeletonInvoiceForm from "./components/SkeletonInvoiceForm";
import SkeletonPaymentForm from "./components/SkeletonPaymentForm";
import {
  LeftSidebarWrapper,
  MainWrapper,
  RightSidebarWrapper,
} from "./components/Wrapper";
import { getDetails } from "./helpers";
import { StripeCustomer } from "./schema";
import { useCheckout } from "./useCheckout";

interface Props {
  openCheckoutDialog?: CheckoutState;
  inDialog?: boolean;
  onSuccess?: () => void;
}

const StripeCheckout = ({
  openCheckoutDialog,
  onSuccess,
  inDialog = false,
}: Props) => {
  const location = useLocation();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const {
    subscription,
    setSubscription,
    setOpenCheckoutDialog,
    setUpgradeDialogSource,
    plans,
    addOns,
  } = useContext(SubscriptionContext);
  const {
    plan,
    customer,
    setCustomer,
    promoCode,
    noNeedsToPay,
    options,
    setOptions,
    addOnItems,
    setRequiresBillingAddress,
    invoice,
    setPlan,
    setPageSource,
    setSource,
    setCoupon,
    setDomainInfo,
    invoiceLoading,
    setPromoCode,
    setAddOnItems,
    setSplashPageSource,
  } = useContext(CheckoutContext);

  const { state } = useLocation();

  const getState = () => {
    if (openCheckoutDialog?.plan) {
      return openCheckoutDialog;
    } else {
      return { ...state } as CheckoutState;
    }
  };
  const {
    plan: planFromState,
    pageSource,
    source,
    coupon: emailCoupon,
    domainInfo,
    splashPageSource,
  } = getState();

  const isInstagramOnboardingFlow = splashPageSource === SplashPage.instagram;
  const isOOOFlow = splashPageSource === SplashPage.ooo;
  const isProBundleFlow = splashPageSource === SplashPage.pro_bundle;

  const isDialogOpen = Boolean(openCheckoutDialog?.plan);

  const [loading, setLoading] = useState(false);
  const [submitAddressLoading, setSubmitAddressLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { updatePreviewInvoice } = useCheckout();

  const isVaPlan = plan === Plans.VA || plan === Plans.VA_YEARLY;
  const isAddOnItem = addOns && plan && plan in addOns;
  const isNonRecurringAddOnItem = plan === AddOnItemName.support_call;
  const isSwitchingSubscription =
    subscription?.subscription?.status === SubscriptionType.ACTIVE &&
    !isAddOnItem;
  const [hideVaBundle, setHideVaBundle] = useState(false);

  const details = getDetails(plan, plans, addOns, splashPageSource);

  const getReturnUrl = () => {
    if (plan === AddOnItemName.support_call) {
      return `${window.location.protocol}//${window.location.host}/${routes.vaVideoOverview}?checkout=success`;
    } else if (isVaPlan) {
      return `${window.location.protocol}//${window.location.host}/${routes.onboardingVaInfluencer}?checkout=success`;
    } else if (isProBundleFlow) {
      return `${window.location.protocol}//${window.location.host}/${routes.welcomeOnboarding}?checkout=success&source=${getSource()}`;
    } else {
      return `${window.location.protocol}//${window.location.host}${
        pageSource || routes.settings
      }?checkout=success`;
    }
  };
  const returnUrl = getReturnUrl();

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setHideVaBundle(Boolean(res.organizationVaProfile));
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getOrCreateCustomer = async (organizationId: number) => {
    setLoading(true);
    if (!plan) return;
    try {
      const { customer } = await fetcherAuth(
        getToken,
        `/api/organization/${organizationId}/payments/customer`,
        "POST",
      );
      if (customer?.address?.country && customer?.address?.postal_code) {
        setRequiresBillingAddress(false);
      }
      setCustomer(customer);

      let updatedPromoCode = promoCode;
      if (!promoCode.id && customer.affiliate_promo_code) {
        setPromoCode(customer.affiliate_promo_code);
        updatedPromoCode = customer.affiliate_promo_code;
      }
      const presetAddOns = getPresetAddOnItems();
      updatePreviewInvoice({
        plan,
        organizationId,
        customer,
        coupon: updatedPromoCode,
        addOnItems: presetAddOns,
        splashPageSource,
      });
    } catch (error) {
      setErrorAlert(error);
      setRequiresBillingAddress(true);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (e: CustomEvent, paymentMethodId?: string) => {
    e.preventDefault();
    if (!currentOrg?.id || !plan) return;
    setSubmitAddressLoading(true);
    try {
      const { customer: updatedCustomer } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/customer`,
        "PUT",
        {},
        {
          customerId: customer?.id,
          country: customer?.address?.country,
          postalCode: customer?.address?.postal_code,
          line1: customer?.address?.line1,
          line2: customer?.address?.line2,
          state: customer?.address?.state,
          city: customer?.address?.city,
          paymentMethodId,
          source,
        },
      );
      setCustomer(updatedCustomer);
      updatePreviewInvoice({
        plan: plan as AddOnItemName | Plans,
        organizationId: currentOrg?.id,
        customer: updatedCustomer,
      });
      setRequiresBillingAddress(false);
    } catch (error) {
      setErrorAlert(error);
      setRequiresBillingAddress(true);
    } finally {
      setSubmitAddressLoading(false);
    }
  };

  const generateIntentSecret = async () => {
    if (!currentOrg) return;
    try {
      const { clientSecret } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/intent`,
        "POST",
      );
      setOptions({
        clientSecret,
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const createSubscription = async () => {
    const url = isAddOnItem
      ? `/api/organization/${currentOrg?.id}/payments-add-ons/subscribe`
      : `/api/organization/${currentOrg?.id}/payments/subscription/create`;
    setConfirmLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        url,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          splashPageSource,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          domainInfo,
        },
      );
      const subscription = isAddOnItem ? res.addOnItem : res.subscription;
      await postSuccessCheckout(subscription);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const switchSubscription = async () => {
    try {
      setConfirmLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription/switch`,
        "POST",
        {},
        {
          plan,
          stripeCustomerId: customer?.id,
          source,
          pageSource,
          isInstagramOnboardingFlow,
          amountDue: invoice?.amount_due,
          promoCode: promoCode?.id,
          couponName: promoCode?.name,
          bundledAddOns: addOnItems,
          splashPageSource,
        },
      );
      await postSuccessCheckout(res.subscription);
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev: StripeCustomer) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const payOneItemInvoice = async () => {
    if (!invoice?.id) return;
    try {
      setConfirmLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/invoice/pay`,
        "POST",
        {},
        {
          invoiceId: invoice.id,
          stripeCustomerId: customer?.id,
          itemName: plan,
          source,
        },
      );
      if (res.addOnItem) {
        window.open(returnUrl, "_self");
      }
    } catch (error) {
      if (error?.message?.includes("payment method")) {
        setCustomer((prev) => ({
          ...prev,
          payment_method: undefined,
        }));
      }
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (isNonRecurringAddOnItem) {
      await payOneItemInvoice();
    } else if (isSwitchingSubscription) {
      await switchSubscription();
    } else {
      await createSubscription();
    }
  };

  const trackSubscription = async (abandonedCheckout?: boolean) => {
    if (!plan) return;
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/payments/subscription/track`,
      "POST",
      {},
      {
        plan,
        isAbandoned: abandonedCheckout,
        isInstagramOnboardingFlow,
      },
    );
  };

  const postSuccessCheckout = async (subscription: StripeSubscription) => {
    if (subscription?.status !== SubscriptionType.ACTIVE) {
      // Failed payment, redirect to Settings page so they can retry
      window.open(
        `${window.location.protocol}//${window.location.host}/dashboard/settings?checkout=failed_payment`,
        "_self",
      );
      return;
    }

    if (
      // @ts-ignore
      typeof window.fbq !== "undefined" &&
      // @ts-ignore
      typeof window.fbq === "function"
    ) {
      // @ts-ignore
      fbq("track", "Subscribe", {
        value: details?.price?.toFixed(2),
        currency: "USD",
        predicted_ltv: details?.price
          ? (details?.price * 5).toFixed(2)
          : "0.00",
      });
    }

    if (isVaPlan || !isDialogOpen) {
      window.open(returnUrl, "_self");
    } else {
      await fetchUserInfo();
      setOpenCheckoutDialog(null);
      setUpgradeDialogSource("");
      searchParams.set("checkout", "success");
      setSearchParams(searchParams);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const getPresetAddOnItems = () => {
    // Return the right add on items based on the SplashPage source.
    if (isOOOFlow && plan !== AddOnItemName.oui_studio) {
      return [
        {
          name: AddOnItemName.oui_studio,
          quantity: 1,
        },
      ];
    }
    if (isProBundleFlow && plan !== AddOnItemName.partnership_data) {
      return [
        {
          name: AddOnItemName.partnership_data,
          quantity: 1,
        },
      ];
    }
    return [];
  };

  useEffect(() => {
    if (emailCoupon) setCoupon(emailCoupon);
    if (source) setSource(source);
    if (pageSource) setPageSource(pageSource);
    if (domainInfo) setDomainInfo(domainInfo);
    if (splashPageSource) setSplashPageSource(splashPageSource);
    const presetAddOns = getPresetAddOnItems();
    if (presetAddOns.length > 0) {
      setAddOnItems(presetAddOns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailCoupon, source, pageSource, domainInfo, isOOOFlow]);

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentOrg?.id && planFromState) {
      trackSubscription();
      setPlan(planFromState as Plans | AddOnItemName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, planFromState]);

  useEffect(() => {
    if (!customer?.id && currentOrg?.id && plan) {
      getOrCreateCustomer(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, plan]);

  // Generate a SetupIntent (ask for their payment method without charging)
  // This will be stored on the Customer object by Stripe so that we can automatically
  // subscribe them.
  const needsSetupIntentSecret =
    !options?.clientSecret && currentOrg?.id && customer?.id;

  useEffect(() => {
    if (needsSetupIntentSecret && plan) generateIntentSecret();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSetupIntentSecret, plan]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) return <></>;

  const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  const handleBack = () => {
    trackSubscription(true);
    if (splashPageSource === SplashPage.pro_bundle) {
      navigate(`/${routes.proBundle}${location.search}`);
    } else if (isDialogOpen) {
      searchParams.set("checkout", "failed");
      setSearchParams(searchParams);
      setOpenCheckoutDialog(null);
    } else if (pageSource) {
      navigate(`${pageSource}?checkout=failed`);
    } else {
      navigate(`${routes.settings}?checkout=failed`);
    }
  };

  return (
    <MainWrapper inDialog={inDialog}>
      <LeftSidebarWrapper inDialog={inDialog} isOOOFlow={isOOOFlow}>
        <Grid item xs={12} xl={isDialogOpen ? 12 : 9}>
          <Grid item xs={12}>
            {!inDialog && !isInstagramOnboardingFlow && (
              <Button
                sx={{ color: "black", textTransform: "none", fontSize: 12 }}
                onClick={handleBack}
              >
                <Box
                  sx={{ mr: 1 }}
                  component="i"
                  className="fa-regular fa-arrow-left"
                />{" "}
                Back{" "}
              </Button>
            )}
          </Grid>

          <PlanDetails
            isSwitchingSubscription={isSwitchingSubscription}
            details={details}
          />

          {invoiceLoading || !invoice ? (
            <SkeletonInvoiceForm />
          ) : (
            <InvoicePreview
              displayName={details?.display_name}
              productId={details?.product || ""}
              hideVaBundle={hideVaBundle}
            />
          )}
        </Grid>
      </LeftSidebarWrapper>

      <RightSidebarWrapper inDialog={inDialog}>
        {loading || !invoice ? (
          <SkeletonPaymentForm />
        ) : (
          <>
            {customer?.payment_method ? (
              <ConfirmPaymentForm
                onConfirm={handleConfirm}
                confirmLoading={confirmLoading}
                customer={customer}
                noNeedsToPay={noNeedsToPay}
              />
            ) : (
              <Elements stripe={stripe} options={options}>
                <Grid
                  item
                  xs={12}
                  md={isDialogOpen ? 12 : 6}
                  xl={isDialogOpen ? 12 : 4}
                >
                  <PaymentForm
                    handleConfirm={handleConfirm}
                    updateCustomer={updateCustomer}
                    submitAddressLoading={submitAddressLoading}
                    returnUrl={returnUrl}
                    setUpClientSecret={options?.clientSecret}
                    trackSubscription={trackSubscription}
                    isInstagramOnboardingFlow={isInstagramOnboardingFlow}
                  />
                </Grid>
              </Elements>
            )}
          </>
        )}
      </RightSidebarWrapper>
    </MainWrapper>
  );
};

export default StripeCheckout;
